import { useNavigate } from "react-router-dom";
import type { BalanceFailureAlertState } from "@/hooks/useBalanceFailureAlert";

function formatWhen(iso?: string | null): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("ko-KR", {
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

/**
 * 최근 발송 실패 사유가 "잔액 부족"일 때 대시보드/통계 화면 상단에 노출하는 공용 경고 배너.
 */
export default function BalanceFailureBanner({ state }: { state: BalanceFailureAlertState }) {
  const navigate = useNavigate();
  const { hasAlert, count, lastFailedAt, refresh } = state;

  if (!hasAlert) return null;

  return (
    <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 px-4 md:px-5 py-4">
      <div className="flex items-start gap-3.5">
        <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/[0.06] text-amber-400 shrink-0">
          <i className="ri-alert-line text-xl"></i>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-bold text-amber-300">발신 계정 잔액 부족으로 발송이 실패했습니다</p>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 whitespace-nowrap">
              최근 실패 {count.toLocaleString()}건
            </span>
          </div>

          <p className="text-gray-400 text-xs leading-relaxed mt-1.5">
            문자 발송 대행 계정의 잔액이 부족해 최근 발송이 실패했습니다. 잔액이 충전되면 발송이 정상화되며,
            실패한 발송 크레딧은 자동으로 환불됩니다.
          </p>

          <div className="flex items-center gap-3 flex-wrap mt-2">
            {lastFailedAt && (
              <span className="flex items-center gap-1.5 text-[11px] text-gray-600">
                <i className="ri-time-line text-xs"></i>
                최근 {formatWhen(lastFailedAt)}
              </span>
            )}
            <button
              onClick={refresh}
              className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-200 cursor-pointer transition-colors whitespace-nowrap"
            >
              <i className="ri-refresh-line text-xs"></i>
              다시 확인
            </button>
          </div>
        </div>

        <button
          onClick={() => navigate("/dashboard/support")}
          className="shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white/[0.08] hover:bg-white/[0.14] text-gray-200 text-xs font-semibold cursor-pointer whitespace-nowrap transition-all"
        >
          <i className="ri-customer-service-2-line text-sm"></i>
          문의하기
        </button>
      </div>
    </div>
  );
}